import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Building2, Users, Briefcase, Clock, CheckCircle, XCircle,
  ChevronRight, Calendar, Mail, Phone, MapPin, RefreshCw, Shield, ShieldOff, Star
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import Loading from '../../components/Loading';

const AdminBusinesses = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalCount: 0,
    totalPages: 0
  });

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [planFilter, setPlanFilter] = useState('All');

  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [extendReason, setExtendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersModalBusiness, setUsersModalBusiness] = useState(null);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  useEffect(() => {
    loadBusinesses();
  }, [pagination.page, statusFilter, planFilter]);

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getBusinesses({
        search: search || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        plan: planFilter !== 'All' ? planFilter : undefined,
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      
      setBusinesses(response.data.businesses);
      setPagination(prev => ({
        ...prev,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages
      }));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadBusinesses();
  };

  const handleExtendTrial = async () => {
    if (!selectedBusiness) return;
    setActionLoading(true);
    try {
      await adminApi.extendTrial(selectedBusiness.id, {
        additionalDays: extendDays,
        reason: extendReason
      });
      setShowExtendModal(false);
      setSelectedBusiness(null);
      setExtendDays(7);
      setExtendReason('');
      loadBusinesses();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to extend trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (business) => {
    if (!confirm(`${business.isActive ? 'Deactivate' : 'Activate'} ${business.name}?`)) return;
    try {
      await adminApi.toggleBusinessActive(business.id);
      loadBusinesses();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update business');
    }
  };

  const handleToggleFoundingMember = async (business) => {
    const action = business.isFoundingMember ? 'Remove founding member status from' : 'Mark as founding member:';
    if (!confirm(`${action} ${business.name}?\n\nThis affects which Stripe price is used on their next subscription or plan change.`)) return;
    try {
      const response = await adminApi.toggleFoundingMember(business.id);
      alert(response.data.message);
      loadBusinesses();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update founding member status');
    }
  };

  const handleOpenUsersModal = async (business) => {
    setUsersModalBusiness(business);
    setShowUsersModal(true);
    setUsersLoading(true);
    try {
      const response = await adminApi.getBusinessUsers(business.id);
      setBusinessUsers(response.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load users');
      setBusinessUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleResetMfa = async (user) => {
    if (!confirm(`Reset MFA for ${user.email}?`)) return;
    try {
      const response = await adminApi.resetUserMfa(user.id);
      alert(response.data.message);
      const usersResponse = await adminApi.getBusinessUsers(usersModalBusiness.id);
      setBusinessUsers(usersResponse.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reset MFA');
    }
  };

  const getStatusBadge = (business) => {
    if (business.subscriptionStatus === 'Active') {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Active</span>;
    }
    if (business.subscriptionStatus === 'Trial') {
      const daysLeft = business.trialDaysRemaining;
      if (daysLeft <= 0) {
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Expired</span>;
      }
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Trial ({daysLeft}d)</span>;
    }
    if (business.subscriptionStatus === 'Cancelled') {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Cancelled</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">{business.subscriptionStatus}</span>;
  };

  const getPlanBadge = (plan) => {
    if (!plan) return null;
    const colors = {
      Solo: 'bg-gray-100 text-gray-700',
      Pro: 'bg-blue-100 text-blue-700',
      Enterprise: 'bg-purple-100 text-purple-700'
    };
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[plan] || 'bg-gray-100'}`}>{plan}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Businesses</h1>
          <p className="text-sm text-gray-600">{pagination.totalCount} total subscribers</p>
        </div>
        <button onClick={loadBusinesses} className="btn btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto">
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 sm:p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search name, email, city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="All">All Status</option>
              <option value="Trial">Trial</option>
              <option value="Active">Active</option>
              <option value="Expired">Expired</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            <select
              value={planFilter}
              onChange={(e) => { setPlanFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="All">All Plans</option>
              <option value="Solo">Solo</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </select>

            <button type="submit" className="btn btn-primary text-sm px-4">Search</button>
          </div>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6 text-sm">{error}</div>
      )}

      {loading ? (
        <Loading />
      ) : (
        <>
          {/* Business List */}
          <div className="space-y-3">
            {businesses.length === 0 ? (
              <div className="card p-8 text-center text-gray-500">No businesses found.</div>
            ) : (
              businesses.map((business) => (
                <div
                  key={business.id}
                  className={`card p-3 sm:p-4 ${!business.isActive ? 'opacity-60' : ''}`}
                >
                  {/* Header Row */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 mb-1">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{business.name}</h3>
                        {getStatusBadge(business)}
                        {getPlanBadge(business.subscriptionPlan)}
                        {business.isFoundingMember && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                            <Star size={10} fill="currentColor" />
                            Founder
                          </span>
                        )}
                        {!business.isActive && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Disabled</span>
                        )}
                      </div>
                      
                      {/* Contact Info - Wrap on mobile */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1 truncate">
                          <Mail size={12} />
                          <span className="truncate max-w-[150px] sm:max-w-none">{business.ownerEmail}</span>
                        </span>
                        {business.city && (
                          <span className="flex items-center gap-1">
                            <MapPin size={12} />
                            {business.city}, {business.state}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleOpenUsersModal(business)}
                        className="px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 flex items-center gap-1"
                      >
                        <Shield size={12} />
                        Users
                      </button>
                      <button
                        onClick={() => handleToggleFoundingMember(business)}
                        className={`px-2 py-1 text-xs font-medium rounded flex items-center gap-1 ${
                          business.isFoundingMember
                            ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                            : 'text-gray-600 bg-gray-50 hover:bg-gray-100'
                        }`}
                        title={business.isFoundingMember ? 'Remove founding member status' : 'Mark as founding member'}
                      >
                        <Star size={12} fill={business.isFoundingMember ? 'currentColor' : 'none'} />
                        Founder
                      </button>
                      {business.subscriptionStatus === 'Trial' && (
                        <button
                          onClick={() => { setSelectedBusiness(business); setShowExtendModal(true); }}
                          className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                        >
                          Extend
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(business)}
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          business.isActive ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-green-600 bg-green-50 hover:bg-green-100'
                        }`}
                      >
                        {business.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <Link
                        to={`/admin/businesses/${business.id}`}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <ChevronRight size={16} />
                      </Link>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Users size={12} />
                        {business.customerCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} />
                        {business.jobCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(business.createdAt)}
                      </span>
                    </div>

                    {/* Mobile Actions */}
                    <div className="sm:hidden flex items-center gap-1">
                      <button
                        onClick={() => handleOpenUsersModal(business)}
                        className="p-1.5 text-purple-600 bg-purple-50 rounded"
                      >
                        <Shield size={14} />
                      </button>
                      <button
                        onClick={() => handleToggleFoundingMember(business)}
                        className={`p-1.5 rounded ${
                          business.isFoundingMember
                            ? 'text-amber-600 bg-amber-50'
                            : 'text-gray-400 bg-gray-50'
                        }`}
                        title={business.isFoundingMember ? 'Remove founding member status' : 'Mark as founding member'}
                      >
                        <Star size={14} fill={business.isFoundingMember ? 'currentColor' : 'none'} />
                      </button>
                      <Link to={`/admin/businesses/${business.id}`} className="p-1.5 text-gray-400">
                        <ChevronRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6">
              <p className="text-xs text-gray-500">
                {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="px-3 py-1.5 text-sm">{pagination.page} / {pagination.totalPages}</span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Extend Trial Modal */}
      {showExtendModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Extend Trial: {selectedBusiness.name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                <select
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  placeholder="Optional..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => { setShowExtendModal(false); setSelectedBusiness(null); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
                Cancel
              </button>
              <button onClick={handleExtendTrial} disabled={actionLoading} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm">
                {actionLoading ? 'Extending...' : 'Extend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users/MFA Modal */}
      {showUsersModal && usersModalBusiness && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Users - {usersModalBusiness.name}
              </h3>
              <button onClick={() => { setShowUsersModal(false); setUsersModalBusiness(null); setBusinessUsers([]); }} className="p-1 hover:bg-gray-100 rounded">
                <XCircle size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {usersLoading ? (
                <div className="py-8 text-center text-gray-500 text-sm">Loading users...</div>
              ) : businessUsers.length === 0 ? (
                <div className="py-8 text-center text-gray-500 text-sm">No users found</div>
              ) : (
                <div className="space-y-3">
                  {businessUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1 mb-0.5">
                          <span className="font-medium text-gray-900 text-sm">{user.fullName}</span>
                          <span className="px-1.5 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">{user.role}</span>
                          {user.mfaEnabled && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded flex items-center gap-0.5">
                              <Shield size={10} />
                              MFA
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>

                      {user.mfaEnabled && (
                        <button
                          onClick={() => handleResetMfa(user)}
                          className="px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 rounded hover:bg-orange-100 flex items-center gap-1 flex-shrink-0 ml-2"
                        >
                          <ShieldOff size={12} />
                          Reset
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end p-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => { setShowUsersModal(false); setUsersModalBusiness(null); setBusinessUsers([]); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinesses;